/**
 * Sprint 23.5 — HireDraftFooter.
 *
 * Cancel + Hire buttons. Themed verb on the primary button (Hire / Recruit /
 * Bring on / Spin up / Add). Disabled until name + role are set; spinner
 * during the hire round-trip.
 */

import type { CSSProperties } from "react";

export interface HireDraftFooterProps {
  hireLabel: string;
  hireName: string;
  canHire: boolean;
  busy: boolean;
  onCancel: () => void;
  onHire: () => void;
}

export function HireDraftFooter({
  hireLabel,
  hireName,
  canHire,
  busy,
  onCancel,
  onHire,
}: HireDraftFooterProps) {
  const hireText = busy
    ? `${hireLabel}ing${hireName ? ` ${hireName}` : ""}…`
    : `${hireLabel}${hireName ? ` ${hireName}` : ""}`;

  return (
    <div style={wrap}>
      <button type="button" onClick={onCancel} style={cancel} disabled={busy}>
        Cancel
      </button>
      <button
        type="button"
        onClick={onHire}
        disabled={!canHire || busy}
        style={canHire && !busy ? hireActive : hireInactive}
      >
        {busy ? (
          <i
            className="ti ti-loader-2"
            style={{ fontSize: 14, animation: "spin 0.9s linear infinite" }}
            aria-hidden="true"
          />
        ) : null}
        {hireText}
      </button>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const wrap: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "12px 16px",
  borderTop: "var(--kuvio-border-default)",
  background: "var(--color-background-primary)",
};

const cancel: CSSProperties = {
  fontSize: 13,
  padding: "8px 14px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-md)",
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const hireActive: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: 13,
  fontWeight: 500,
  padding: "8px 14px",
  border: "none",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const hireInactive: CSSProperties = {
  ...hireActive,
  opacity: 0.45,
  cursor: "not-allowed",
};
